import { useMemo } from 'react'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'
import { useLocation } from '../contexts/LocationContext'
import { useData } from '../contexts/DataContext'
import { useFilters } from '../contexts/FilterContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorBoundary from '../components/common/ErrorBoundary'
import {
  calculateFacilityLevelDistribution,
  calculateServiceCoverage,
  calculateInfrastructureReadiness,
  calculateSupplyChainStatus,
  calculateStaffingAnalysis,
  generateLocationInsights,
} from '../utils/chartUtils'
import {
  calculateOwnershipDistribution,
  calculateEducationLevelDistribution,
  calculateParishDistribution,
  calculateOwnershipByLevel,
  generateEducationInsights,
  calculateEnrollmentDistribution,
  calculateGenderBalance,
  calculateTeacherQualifications,
  calculateInfrastructureReadiness as calculateEducationInfrastructureReadiness,
  calculateResourceRatios,
  calculateTeacherStudentRatios,
  calculateInfrastructureScores,
  calculateWASHCompliance,
  calculateInfrastructureGapMatrix,
} from '../utils/educationChartUtils'
import HealthCharts from '../components/charts/HealthCharts'
import EducationCharts from '../components/charts/EducationCharts'

function ChartsView() {
  const { facilities, total, filtered } = useFilteredFacilities()
  const { loading } = useData()
  const { activeCategory } = useFilters()
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    findLocationById,
  } = useLocation()

  // Generate current location name for display
  const currentLocationName = useMemo(() => {
    if (isViewingAllDistricts()) return 'All Districts (National View)'
    if (selectedVillage) return 'Selected Village'
    if (selectedParish) return 'Selected Parish'
    if (selectedSubcounty) return 'Selected Subcounty'
    if (selectedDistrict) {
      const districtData = findLocationById(selectedDistrict)
      return districtData
        ? `${districtData.name} District`
        : 'Selected District'
    }
    return 'All Locations'
  }, [
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    findLocationById,
  ])

  // Calculate district aggregations for multi-district mode
  const districtAggregations = useMemo(() => {
    if (!isViewingAllDistricts() || !facilities.length) return null

    const districtGroups = facilities.reduce((acc, facility) => {
      const district = facility.district
      if (!district) return acc

      if (!acc[district]) {
        acc[district] = []
      }
      acc[district].push(facility)
      return acc
    }, {})

    return Object.entries(districtGroups)
      .map(([districtName, districtFacilities]) => ({
        name: districtName,
        facilities: districtFacilities,
        facilityCount: districtFacilities.length,
        facilityLevels: calculateFacilityLevelDistribution(districtFacilities),
        serviceCoverage: calculateServiceCoverage(districtFacilities),
        infrastructureReadiness:
          calculateInfrastructureReadiness(districtFacilities),
        supplyChainStatus: calculateSupplyChainStatus(districtFacilities),
        staffingAnalysis: calculateStaffingAnalysis(districtFacilities),
      }))
      .sort((a, b) => b.facilityCount - a.facilityCount) // Sort by facility count desc
  }, [facilities, isViewingAllDistricts])

  // Calculate chart data based on category
  const chartData = useMemo(() => {
    if (!facilities.length) return null

    if (activeCategory === 'education') {
      // Education-specific data processing
      return {
        ownershipDistribution: calculateOwnershipDistribution(facilities),
        educationLevelDistribution:
          calculateEducationLevelDistribution(facilities),
        parishDistribution: calculateParishDistribution(facilities),
        ownershipByLevel: calculateOwnershipByLevel(facilities),
        enrollmentDistribution: calculateEnrollmentDistribution(facilities),
        genderBalance: calculateGenderBalance(facilities),
        teacherQualifications: calculateTeacherQualifications(facilities),
        infrastructureReadiness:
          calculateEducationInfrastructureReadiness(facilities),
        // New infrastructure analytics
        resourceRatios: calculateResourceRatios(facilities),
        teacherStudentRatios: calculateTeacherStudentRatios(facilities),
        infrastructureScores: calculateInfrastructureScores(facilities),
        washCompliance: calculateWASHCompliance(facilities),
        infrastructureGapMatrix: calculateInfrastructureGapMatrix(facilities),
        insights: generateEducationInsights(facilities, currentLocationName),
      }
    }

    // Health-specific data processing
    if (isViewingAllDistricts() && districtAggregations) {
      // National-level aggregated data for multi-district view
      return {
        facilityLevels: calculateFacilityLevelDistribution(facilities),
        serviceCoverage: calculateServiceCoverage(facilities),
        infrastructureReadiness: calculateInfrastructureReadiness(facilities),
        supplyChainStatus: calculateSupplyChainStatus(facilities),
        staffingAnalysis: calculateStaffingAnalysis(facilities),
        insights: generateLocationInsights(facilities, currentLocationName),
        districtComparisons: districtAggregations, // Additional data for multi-district charts
      }
    }

    // Standard location-specific data
    return {
      facilityLevels: calculateFacilityLevelDistribution(facilities),
      serviceCoverage: calculateServiceCoverage(facilities),
      infrastructureReadiness: calculateInfrastructureReadiness(facilities),
      supplyChainStatus: calculateSupplyChainStatus(facilities),
      staffingAnalysis: calculateStaffingAnalysis(facilities),
      insights: generateLocationInsights(facilities, currentLocationName),
    }
  }, [
    facilities,
    currentLocationName,
    isViewingAllDistricts,
    districtAggregations,
    activeCategory,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
  ])

  // Show loading if facilities are currently being loaded
  if (loading[`facilities_${activeCategory}`]) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading facility data...</p>
        </div>
      </div>
    )
  }

  if (!total) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📊</div>
          <p>No data available for charts</p>
          <p className="text-sm mt-2">
            Select a location or check your data source
          </p>
        </div>
      </div>
    )
  }

  if (filtered === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">🔍</div>
          <p>No facilities match your current filters</p>
          <p className="text-sm mt-2">
            Try adjusting your location or filter selections
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with location insights */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          📊{' '}
          {activeCategory === 'education'
            ? 'Education Analytics'
            : 'Healthcare Analytics'}{' '}
          for {currentLocationName}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chartData?.insights.map((insight, index) => (
            <div key={index} className="bg-white p-3 rounded-md shadow-sm">
              <p className="text-sm text-gray-700 capitalize">{insight}</p>
            </div>
          ))}
        </div>
        {filtered !== total && (
          <div className="mt-3 text-sm text-gray-600">
            <span className="font-medium">Note:</span> Charts show {filtered} of{' '}
            {total} facilities based on current filters
          </div>
        )}
      </div>

      {/* Education Charts */}
      {activeCategory === 'education' && (
        <ErrorBoundary
          title="Education Charts Error"
          fallbackMessage="There was an error loading the education charts. Please try refreshing the page or selecting a different location."
        >
          <EducationCharts chartData={chartData} filtered={filtered} />
        </ErrorBoundary>
      )}

      {/* Health Charts */}
      {activeCategory === 'health' && (
        <ErrorBoundary
          title="Health Charts Error"
          fallbackMessage="There was an error loading the health charts. Please try refreshing the page or selecting a different location."
        >
          <HealthCharts chartData={chartData} filtered={filtered} />
        </ErrorBoundary>
      )}

      {/* Chart Controls Footer */}
      <div className="text-sm text-gray-600 bg-white p-4 rounded border">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">Location:</span> {currentLocationName}
          </div>
          <div>
            <span className="font-medium">Last Updated:</span>{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChartsView
